<template>
  <div>
    <breadcrumbs
      :breadcrumb-items="breadcrumbItems"
    />
    <UserToolbar
      v-if="account && Object.keys(account).length !== 0"
      :account="account"
    />
    <b-card>
      <CampaignToolbar
        :campaign="campaign"
        closing
      />
    </b-card>

    <CampaignNavigation v-if="authUserData.role.includes('admin')" />

    <b-card class="card-statistics">
      <div class="card-statistics-tools">
        <div>
          <h4>{{ $t('Statistics') }}</h4>
        </div>
        <div v-if="!authUserData.is_agency_member">
          <CampaignBalance
            :campaign-data="campaign"
          />
        </div>

      </div>
      <div>
        <div class="d-flex align-items-center">
          <CampaignDateRangeFilter
            :value="rangePicker"
            :is-loading="loading"
            @refresh="getMergedStatsData"
            @input="toggleRange"
          />
        </div>
      </div>
      <div>
        <div
          v-if="loading"
          class="text-center my-3"
        >
          <b-spinner label="Loading..." />
        </div>
        <b-table
          v-else-if="rtbStatsData.length"
          :items="rtbStatsData"
          :fields="rtbFields"
          striped
          responsive
          small
          :sort-by.sync="rtbSortBy"
          :sort-desc.sync="rtbSortDesc"
          :sort-direction="rtbSortDirection"
          show-empty
          foot-clone
          class="text-center mt-2"
        >
          <template #head(date)>
            {{ $t('stats.date') }}
          </template>
          <template #head(impressions)>
            {{ $t('stats.impressions') }}
          </template>
          <template #head(clicks)>
            {{ $t('stats.clicks') }}
          </template>
          <template #head(ctr)>
            CTR
          </template>
          <template #head(pixel)>
            Pixel
          </template>
          <template #head(spent)>
            {{ $t('stats.spent') }}
          </template>

          <template #cell(date)="data">
            {{ data.value ? getFormattedDate(new Date(data.value)) : '' }}
          </template>
          <template #cell(impressions)="data">
            {{ data.value ? new Intl.NumberFormat('en-GB', { style: 'decimal' }).format(data.value) : 0 }}
          </template>
          <template #cell(clicks)="data">
            {{ data.value ? new Intl.NumberFormat('en-GB', { style: 'decimal' }).format(data.value) : 0 }}
          </template>
          <template #cell(ctr)="data">
            {{ data.item.clicks ? new Intl.NumberFormat('en-GB', { style: 'decimal', maximumFractionDigits: 3 }).format(data.item.clicks / data.item.impressions * 100) + '%' : 0 }}
          </template>
          <template #cell(pixel)="data">
            {{ data.value ? new Intl.NumberFormat('en-GB', { style: 'decimal' }).format(data.value) : 0 }}
          </template>
          <template #cell(spent)="data">
            {{ data.value ? currency(campaign.currency || 'USD', data.value) : 0 }}
          </template>

          <template #foot(date)="data">
            <strong :data-label="data.label">{{ $t('Records') }}: {{ rtbStatsData.length }}</strong>
          </template>
          <template #foot(impressions)>
            {{ new Intl.NumberFormat('en-GB', { style: 'decimal' }).format(rtbSum('impressions')) }}
          </template>
          <template #foot(clicks)>
            {{ new Intl.NumberFormat('en-GB', { style: 'decimal' }).format(rtbSum('clicks')) }}
          </template>
          <template #foot(ctr)>
            {{ rtbSum('clicks') ? new Intl.NumberFormat('en-GB', { style: 'decimal', maximumFractionDigits: 3 }).format(rtbSum('clicks') / rtbSum('impressions') * 100) : 0 }}%
          </template>
          <template #foot(pixel)>
            {{ new Intl.NumberFormat('en-GB', { style: 'decimal' }).format(rtbSum('pixel')) }}
          </template>
          <template #foot(spent)>
            {{ currency(campaign.currency || 'USD', rtbSum('spent')) }}
          </template>
        </b-table>
        <div
          v-else
          class="text-center text-muted my-3"
        >
          {{ $t('No data for this period') }}
        </div>
      </div>
    </b-card>

    <div
      v-if="graphicShow"
      id="blocks_stats"
      ref="blocks_stats"
    >
      <b-row v-if="statsBlocksLoading">
        <b-col>
          <b-card
            class="card-browser-states text-center"
          >
            <b-spinner label="Loading..." />
          </b-card>
        </b-col>
      </b-row>
      <b-row
        v-else-if="Object.keys(filterStatsBlocks).length !== 0 && !statsBlocksLoading"
        class="stats-blocks match-height"
      >
        <b-col
          v-if="filterStatsBlocks.imprByCountryCity.length > 1"
          md="4"
        >
          <countries-stats
            :subtitle="blockSubtitle"
            :country-city="filterStatsBlocks.imprByCountryCity"
          />
        </b-col>
        <b-col
          v-if="filterStatsBlocks.imprByBrowserName.length > 1"
          md="4"
        >
          <browser-stats
            :subtitle="blockSubtitle"
            :rows="filterStatsBlocks.imprByBrowserName"
          />
        </b-col>
        <b-col
          v-if="filterStatsBlocks.imprByLanguage.length > 1"
          md="4"
        >
          <language-stats
            :subtitle="blockSubtitle"
            :rows="filterStatsBlocks.imprByLanguage"
          />
        </b-col>
        <b-col
          v-if="filterStatsBlocks.imprByDeviceType.length > 1"
          md="4"
        >
          <devices-stats
            :subtitle="blockSubtitle"
            :rows="filterStatsBlocks.imprByDeviceType"
          />
        </b-col>
        <b-col
          v-if="filterStatsBlocks.imprByOsName.length > 1"
          md="4"
        >
          <os-stats
            :subtitle="blockSubtitle"
            :rows="filterStatsBlocks.imprByOsName"
          />
        </b-col>
        <b-col
          v-if="filterStatsBlocks.imprByPlatform.length > 1"
          md="4"
        >
          <platform-stats
            :subtitle="blockSubtitle"
            :rows="filterStatsBlocks.imprByPlatform"
          />
        </b-col>
        <b-col
          v-if="filterStatsBlocks.imprByUniqueUsers > 0"
          md="3"
        >
          <unique-users-stats
            :subtitle="blockSubtitle"
            :rows="filterStatsBlocks.imprByUniqueUsers"
          />
        </b-col>
        <b-col
          v-if="filterStatsBlocks.imprSiteByRequestUrl.length > 1"
          md="9"
        >
          <requests-stats
            :subtitle="blockSubtitle"
            :rows="filterStatsBlocks.imprSiteByRequestUrl"
          />
        </b-col>
      </b-row>
    </div>
  </div>
</template>
<script>
import { getUserData } from '@/auth/utils'
import Ripple from 'vue-ripple-directive'
import getFormattedDate, { getFormattedDateMonth } from '@/utils/date-formatter'
import currencyFormatter from '@/utils/currency-formatter'
import useJwt from '@/auth/jwt/useJwt'
import {
  BCard,
  BRow,
  BCol,
  BSpinner,
  BTable,
} from 'bootstrap-vue'
import Breadcrumbs from '@/views/ui/breadcrumbs/Breadcrumbs.vue'
import UserToolbar from '@/views/dashboard/users/UserToolbar.vue'
import CampaignToolbar from '@/views/dashboard/campaigns/CampaignToolbar.vue'
import CountriesStats from '@/views/ui/stats-blocks/CountriesStats.vue'
import BrowserStats from '@/views/ui/stats-blocks/BrowserStats.vue'
import LanguageStats from '@/views/ui/stats-blocks/LanguageStats.vue'
import DevicesStats from '@/views/ui/stats-blocks/DevicesStats.vue'
import OsStats from '@/views/ui/stats-blocks/OsStats.vue'
import RequestsStats from '@/views/ui/stats-blocks/RequestsStats.vue'
import PlatformStats from '@/views/ui/stats-blocks/PlatformStats.vue'
import UniqueUsersStats from '@/views/ui/stats-blocks/UniqueUsersStats.vue'
import CampaignBalance from './CampaignBalance.vue'
import CampaignNavigation from './CampaignNavigation.vue'
import CampaignDateRangeFilter from './CampaignDateRangeFilter.vue'

export default {
  components: {
    Breadcrumbs,
    UserToolbar,
    BCard,
    BRow,
    BCol,
    BSpinner,
    BTable,
    CampaignToolbar,
    CountriesStats,
    BrowserStats,
    LanguageStats,
    DevicesStats,
    OsStats,
    RequestsStats,
    PlatformStats,
    UniqueUsersStats,
    CampaignDateRangeFilter,
    CampaignBalance,
    CampaignNavigation,
  },
  directives: {
    Ripple,
  },
  data() {
    return {
      loading: false,
      statsBlocksLoading: false,
      breadcrumbItems: [],
      account: {},
      campaign: {},
      blockSubtitle: '',
      authUserData: getUserData(),
      currency: currencyFormatter,
      getFormattedDate,
      getFormattedDateMonth,
      rtbStatsData: [],
      statsBlocks: {
        show: {},
        click: {},
      },
      campaignesstats: {},
      campaignclickesstats: {},
      selectedTypeStats: 'month',
      selectedBlocksTypeStats: 'month',
      currentField: 'show',
      typeText: {
        month: this.$i18n.t('30 days'),
        week: this.$i18n.t('7 days'),
        day: this.$i18n.t('1 day'),
      },
      numDays: {
        month: 30,
        week: 7,
        day: 1,
      },
      graphicShow: false,
      hasPixelData: false,
      isInitialLoad: true,
      rangePicker: [getFormattedDate(new Date(new Date().getFullYear(), new Date().getMonth(), 1)), getFormattedDate(new Date())],
      rtbSortBy: 'date',
      rtbSortDesc: true,
      rtbSortDirection: 'desc',
    }
  },
  computed: {
    filterStatsBlocks() {
      return this.statsBlocks[this.currentField] || {}
    },
    rtbFields() {
      const baseFields = [
        { key: 'date', label: 'Дата', sortable: true },
        { key: 'impressions', label: 'Impressions', sortable: true },
        { key: 'clicks', label: 'Clicks', sortable: true },
        { key: 'ctr', label: 'CTR', sortable: true },
      ]

      // Add Pixel column if there are non-zero pixel values
      if (this.hasPixelData) {
        baseFields.push({ key: 'pixel', label: 'Pixel', sortable: true })
      }

      baseFields.push({ key: 'spent', label: 'Spent', sortable: true })

      return baseFields
    },
    selectedRangeFormated() {
      let dateFrom
      let dateTo
      const selectedRange = this.rangePicker
      if (Array.isArray(selectedRange)) {
        [dateFrom, dateTo] = selectedRange
      } else {
        [dateFrom, , dateTo] = selectedRange.split(' ')
      }

      return {
        from: getFormattedDate(new Date(dateFrom)),
        to: dateTo === undefined ? getFormattedDate(new Date(dateFrom)) : getFormattedDate(new Date(dateTo)),
      }
    },
  },
  watch: {
    currentField() {
      // this.statsBlocksLoading = true
      setTimeout(() => {
        this.statsBlocksLoading = false
      }, 500)
    },
  },
  async created() {
    this.getUser()
    await this.getCampaignData() // Wait for campaign data to load
    this.getMergedStatsData() // Now campaign.type is available
    // this.getCampaignEstats()
    this.getCampaignClickesStats()
    this.blockSubtitle = `${this.selectedRangeFormated.from.replaceAll('-', '.')}-${this.selectedRangeFormated.to.replaceAll('-', '.')}`

    // Mark initial load as complete to allow subsequent range changes
    this.isInitialLoad = false
  },
  methods: {
    toggleRange(selectedDates) {
      this.rangePicker = selectedDates
      this.blockSubtitle = `${this.selectedRangeFormated.from.replaceAll('-', '.')}-${this.selectedRangeFormated.to.replaceAll('-', '.')}`

      // Skip data loading during initial component setup to avoid duplicates
      if (!this.isInitialLoad) {
        this.getMergedStatsData()
        if (this.graphicShow) {
          this.getCampaignEstats()
        }
        this.getCampaignClickesStats()
      }
    },
    toggleGraph() {
      this.graphicShow = !this.graphicShow
      if (this.graphicShow) {
        this.getCampaignEstats()
        const firstTab = document.querySelector('.nav-tabs > li > a')
        if (firstTab && this.currentField === 'amount') {
          firstTab.click()
        }
        this.scrollToTop('#advanced_stats')
      }
    },
    async getMergedStatsData() {
      this.loading = true
      try {
        // Load Phoenix data (using the same logic as CampaignV2.vue)
        let params
        let reqParams
        let phoenixResponse = { data: { items: [] } }

        if (!this.campaign.type) {
          return
        }

        try {
          if (this.campaign.type !== 'default' && this.campaign.type !== 'news' && this.campaign.type !== 'video') {
            // For non-standard campaign types (e.g. eCPM)
            params = {
              campaign_id: this.$route.params.id,
              type: 'daily',
              ad_type: this.campaign.type,
            }
            reqParams = Object.assign(params, this.selectedRangeFormated)
            phoenixResponse = await useJwt.getCampaignStatsEcpm(reqParams)
          } else {
            // For standard types (default, news)
            params = {
              campaign_id: this.$route.params.id,
              type: 'daily',
            }
            reqParams = Object.assign(params, this.selectedRangeFormated)
            phoenixResponse = await useJwt.getCampaignStatsByTypeV2(reqParams)
          }
        } catch (phoenixError) {
          // If Phoenix is unavailable, continue with RTB data only
        }

        // Load RTB data (like RtbStatsOverview page)
        const rtbParams = {
          campaign_id: this.$route.params.id,
          from: this.selectedRangeFormated.from,
          to: this.selectedRangeFormated.to,
          type: 'daily',
        }

        let rtbResponse = { data: { items: [] } }
        try {
          rtbResponse = await useJwt.getPhoenixDspCampaign(rtbParams)
        } catch (rtbError) {
          // If RTB is unavailable, continue with Phoenix data only
        }

        // Load Pixel data
        const pixelParams = {
          campaign_id: this.$route.params.id,
          from: this.selectedRangeFormated.from,
          to: this.selectedRangeFormated.to,
        }

        let pixelResponse = { data: { items: [] } }
        try {
          pixelResponse = await useJwt.getCampaignPixelStats(pixelParams)
        } catch (pixelError) {
          // If pixel stats are unavailable, continue without pixel data
        }

        // Merge data by date
        const phoenixData = phoenixResponse.data.items || []
        const rtbData = rtbResponse.data.items || []
        const pixelData = pixelResponse.data.items || []

        // If no data in both Phoenix and RTB, return empty array
        if (phoenixData.length === 0 && rtbData.length === 0) {
          this.rtbStatsData = []
          return
        }

        // Create Maps for fast data lookup by date
        const phoenixMap = new Map()
        phoenixData.forEach(phoenixItem => {
          phoenixMap.set(phoenixItem.date, phoenixItem)
        })

        const rtbMap = new Map()
        rtbData.forEach(rtbItem => {
          rtbMap.set(rtbItem.date, rtbItem)
        })

        const pixelMap = new Map()
        pixelData.forEach(pixelItem => {
          pixelMap.set(pixelItem.date, pixelItem)
        })

        // Check if there are any non-zero pixel values
        const hasPixelData = pixelData.some(item => item.pixel && item.pixel > 0)

        // Collect all unique dates
        const allDates = new Set([
          ...phoenixData.map(item => item.date),
          ...rtbData.map(item => item.date),
          ...pixelData.map(item => item.date),
        ])

        // Merge data for all dates
        this.rtbStatsData = Array.from(allDates).sort().map(date => {
          const phoenixItem = phoenixMap.get(date) || {}
          const rtbItem = rtbMap.get(date) || {}
          const pixelItem = pixelMap.get(date) || {}

          // Sum revenue from Phoenix (object with different keys)
          const phoenixRevenue = typeof phoenixItem.revenue === 'object'
            ? Object.values(phoenixItem.revenue || {}).reduce((sum, val) => sum + (val || 0), 0)
            : (phoenixItem.revenue || 0)

          // Sum clicks from Phoenix (object with different keys)
          const phoenixClicks = typeof phoenixItem.clicks === 'object'
            ? Object.values(phoenixItem.clicks || {}).reduce((sum, val) => sum + (val || 0), 0)
            : (phoenixItem.clicks || 0)

          const result = {
            date,
            impressions: (phoenixItem.impressions || 0) + (rtbItem.impressions || 0), // Sum Phoenix + RTB impressions
            clicks: phoenixClicks + (rtbItem.clicks || 0), // Sum Phoenix + RTB clicks
            spent: phoenixRevenue + (rtbItem.spent || 0), // Revenue from Phoenix + Spent from RTB
          }

          // Add pixel column only if there are non-zero pixel values
          if (hasPixelData) {
            result.pixel = pixelItem.pixel || 0
          }

          return result
        })

        // Store if we have pixel data for table column display
        this.hasPixelData = hasPixelData
      } catch (error) {
        this.rtbStatsData = []
      } finally {
        this.loading = false
      }
    },
    async getCampaignEstats() {
      this.statsBlocksLoading = true
      this.statsBlocks.show = {}
      const currentDate = new Date()
      const params = {
        from: getFormattedDate(new Date(currentDate.getFullYear(), currentDate.getMonth(), currentDate.getDate() - this.numDays[this.selectedBlocksTypeStats])),
        to: getFormattedDate(currentDate),
      }
      const responseData = await useJwt.getCampaignEsStatsByType(this.$route.params.id, params)
      this.statsBlocks.show = responseData.data.campaignesstats
      this.statsBlocksLoading = false
    },
    async getCampaignClickesStats() {
      this.statsBlocksLoading = true
      this.statsBlocks.click = {}
      const currentDate = new Date()
      const params = {
        from: getFormattedDate(new Date(currentDate.getFullYear(), currentDate.getMonth(), currentDate.getDate() - this.numDays[this.selectedBlocksTypeStats])),
        to: getFormattedDate(currentDate),
      }
      const responseData = await useJwt.getCampaignClickesStats(this.$route.params.id, { from: params.from, to: params.to })
      this.statsBlocks.click = responseData.data.campaignclickesstats
      this.statsBlocksLoading = false
    },
    setField(field) {
      this.currentField = field
    },
    async getCampaignData() {
      const responseData = await useJwt.getCampaign(this.$route.params.id)
      this.campaign = responseData.data.campaign || {}
      this.setBreabcrumbs()
    },
    async getUser() {
      const responseData = await useJwt.getUser(this.$route.params.userId)
      this.account = responseData.data.account || {}
    },
    rtbSum(key) {
      return this.rtbStatsData.reduce((acc, r) => acc + (r[key] || 0), 0)
    },
    setBreabcrumbs() {
      this.breadcrumbItems = [
        {
          text: 'users',
          to: '/users',
        },
        {
          text: this.account.name,
          to: `/users/${this.$route.params.userId}`,
        },
        {
          text: 'campaigns',
          to: `/users/${this.$route.params.userId}/campaigns`,
        },
        {
          text: this.campaign.title,
          active: true,
        },
      ]
    },
  },
}
</script>
<style lang="scss">
@import '@core/scss/vue/libs/vue-flatpicker.scss';
</style>
<style scoped lang="scss">
.card-statistics .card-body {
  min-height: 190px;
}

.card-statistics-tools {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

@media (max-width: 440px) {
  [dir] #app {
    .card-statistics-tools {
      flex-wrap: wrap;
    }
  }
}
</style>
